import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Check } from 'lucide-react';
import { useTrainLogic } from '@/hooks/useTrainLogic';
import TrainMatrixTable, { type TrainMatrixTableRef } from '@/components/company/train/TrainMatrixTable';
import TrainTipsPanel from '@/components/company/train/TrainTipsPanel';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { toast } from 'react-toastify';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';

export default function Train() {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const isReadOnly = useIsReadOnly();
  const matrixRef = useRef<TrainMatrixTableRef>(null);
  const [showPanel, setShowPanel] = useState(true);

  const { isSaving, matrixRows, saveAll, currentTenant } = useTrainLogic();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const trainStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'train the trainer');
  const videoLink = trainStep?.video_link;
  const tips = trainStep?.tips;

  const handleSaveAndNext = async () => {
    const payloads = matrixRef.current?.collectPayloads() ?? [];
    const ok = await saveAll(payloads);
    if (ok) {
      toast.success('Train the Trainer berhasil disimpan');
      navigate(`/${tenant_id}/checklist`);
    }
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Train The Trainer</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Menyusun sistem kaderisasi menggunakan Matrix ASK (Attitude, Skill, Knowledge).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={handleSaveAndNext}
              disabled={isSaving || !currentTenant?.id}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 px-5 mt-4 pb-6">
        <div className="flex-1 min-w-0">
          <TrainMatrixTable ref={matrixRef} rows={matrixRows} tenantId={currentTenant?.id ?? 0} disabled={isReadOnly} />
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0">
            <TrainTipsPanel videoLink={videoLink} tips={tips} />
          </div>
        )}
      </div>
    </section>
  );
}
